// components/ui/button.tsx
import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        // Brand ink — solid "desk bronze". `on-brand` text flips per theme
        // (white on the deep light-mode bronze; dark on the light dark-mode
        // brass) so it stays readable in both modes.
        default: 'bg-brand text-on-brand hover:bg-brand-hover',
        // Variants below use shadcn semantic tokens so colors flip with the
        // theme. Previously hardcoded slate-* values rendered the buttons
        // invisible in light mode (light gray text on white background).
        outline:
          'border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          // Theme-aware disabled styles. Previously these were hardcoded to
          // dark colors (slate-800/400/700), which made disabled buttons
          // illegible in light mode.
          //
          // `disabled:bg-none` cancels any gradient (background-image) the
          // caller layered on top of the default variant. Without it,
          // disabled gradient buttons (e.g. "Select a topic to start" on
          // /drills/mini) keep their cyan→violet gradient because
          // `bg-muted` only sets background-color, not background-image —
          // muted text on a still-vibrant gradient is unreadable.
          'disabled:bg-none disabled:bg-muted disabled:text-muted-foreground disabled:border-input disabled:opacity-100 disabled:cursor-not-allowed',
          'focus-visible:ring-brand/60 focus-visible:ring-offset-background',
          className,
        )}
        ref={ref}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
